/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { Decoder } from 'jpeg-lossless-decoder-js';

/**
 * Transfer syntax UIDs that this module knows how to decompress into
 * the raw little-endian pixel layout the rest of the pipeline expects.
 *
 * Process 14 SV1 (.70) covers the overwhelming majority of clinical
 * CT/MR JPEG Lossless data. Process 14 (.57) is spec-allowed but rare.
 */
export const JpegLosslessTransferSyntaxes = [
    '1.2.840.10008.1.2.4.57',
    '1.2.840.10008.1.2.4.70',
] as const;

export type JpegLosslessTransferSyntax =
    (typeof JpegLosslessTransferSyntaxes)[number];

export function isJpegLosslessTransferSyntax(
    uid: string | undefined
): uid is JpegLosslessTransferSyntax {
    return (
        uid !== undefined &&
        (JpegLosslessTransferSyntaxes as readonly string[]).includes(uid)
    );
}

/**
 * Frame layout dcmjs hands us for encapsulated PixelData.
 *
 * dcmjs returns PixelData as an array of frame buffers when the
 * transfer syntax is encapsulated. Each entry is one frame's complete
 * compressed bitstream (dcmjs already merges fragments when the Basic
 * Offset Table is present, or treats each fragment as a frame when it
 * is not — the latter is fine because clinical CT/MR images are
 * single-frame per file with one fragment).
 */
function toArrayBuffer(entry: unknown): ArrayBuffer {
    // Duck-type rather than `instanceof ArrayBuffer` to survive
    // cross-realm checks in test environments where the constructor
    // reference differs from the one in node_modules.
    if (
        entry !== null &&
        typeof entry === 'object' &&
        typeof (entry as { byteLength?: unknown }).byteLength === 'number' &&
        (entry as { buffer?: unknown }).buffer === undefined
    ) {
        return entry as ArrayBuffer;
    }
    if (ArrayBuffer.isView(entry)) {
        const view = entry as ArrayBufferView;
        return view.buffer.slice(
            view.byteOffset,
            view.byteOffset + view.byteLength
        ) as ArrayBuffer;
    }
    throw new Error('Unexpected encapsulated PixelData entry type');
}

function readEncapsulatedFrames(pixelData: unknown): ArrayBuffer[] {
    if (Array.isArray(pixelData)) {
        const frames: ArrayBuffer[] = [];
        for (const entry of pixelData) {
            if (Array.isArray(entry)) {
                // noCopy mode returns fragments per frame; merge here
                const buffers = entry.map(toArrayBuffer);
                let total = 0;
                for (const f of buffers) {
                    total += f.byteLength;
                }
                const merged = new Uint8Array(total);
                let offset = 0;
                for (const f of buffers) {
                    merged.set(new Uint8Array(f), offset);
                    offset += f.byteLength;
                }
                frames.push(merged.buffer);
            } else {
                frames.push(toArrayBuffer(entry));
            }
        }
        return frames;
    }
    if (
        pixelData !== null &&
        typeof pixelData === 'object' &&
        typeof (pixelData as { byteLength?: unknown }).byteLength === 'number'
    ) {
        return [toArrayBuffer(pixelData)];
    }
    throw new Error(
        'Encapsulated PixelData is missing or has an unsupported shape'
    );
}

/**
 * Decompress a single JPEG Lossless bitstream into a raw pixel buffer
 * laid out as the uncompressed transfer syntaxes would have stored it
 * (little-endian, BitsAllocated-sized samples).
 *
 * `bitsAllocated` selects sample width (1 byte vs 2 bytes per sample).
 * The decoder writes samples in native endianness; we normalise to
 * little-endian afterwards so downstream consumers can treat the
 * buffer identically to ImplicitVR/ExplicitVR LE PixelData.
 */
function decodeOneFrame(
    bitstream: ArrayBuffer,
    bitsAllocated: number
): ArrayBuffer {
    if (bitsAllocated !== 8 && bitsAllocated !== 16) {
        throw new Error(
            `JPEG Lossless decoding requires BitsAllocated 8 or 16, got ${bitsAllocated}`
        );
    }
    const numBytes = bitsAllocated === 8 ? 1 : 2;
    const decoder = new Decoder();
    const decoded: ArrayBuffer = decoder.decompress(
        bitstream,
        0,
        bitstream.byteLength
    ) as ArrayBuffer;

    if (numBytes === 1) {
        return decoded;
    }

    // The decoder emits 16-bit samples in the host's native byte order.
    // DICOM uncompressed transfer syntaxes are little-endian, and
    // downstream readers consume the buffer with DataView using a fixed
    // little-endian flag, so we must guarantee little-endian here.
    if (isLittleEndianHost()) {
        return decoded;
    }
    const view = new DataView(decoded);
    const out = new ArrayBuffer(decoded.byteLength);
    const outView = new DataView(out);
    for (let i = 0; i < decoded.byteLength; i += 2) {
        outView.setUint16(i, view.getUint16(i, false), true);
    }
    return out;
}

let cachedHostEndianness: boolean | null = null;
function isLittleEndianHost(): boolean {
    if (cachedHostEndianness === null) {
        const probe = new ArrayBuffer(2);
        new DataView(probe).setUint16(0, 0x0102, true);
        cachedHostEndianness = new Uint8Array(probe)[0] === 0x02;
    }
    return cachedHostEndianness;
}

/**
 * Decompress encapsulated JPEG Lossless PixelData in a naturalised
 * dcmjs dataset and replace it with the equivalent uncompressed buffer
 * so the rest of the import pipeline can consume it without changes.
 *
 * Mutates `dataset.PixelData` in place; this is safe because the
 * dataset is freshly read from disk and not yet shared.
 *
 * Throws on unsupported BitsAllocated, malformed encapsulation, or
 * decoder errors so the caller can surface an actionable message.
 */
export function decompressJpegLosslessInDataset(dataset: any): void {
    const transferSyntax: unknown =
        dataset?._meta?.TransferSyntaxUID?.Value?.[0];
    if (
        typeof transferSyntax !== 'string' ||
        !isJpegLosslessTransferSyntax(transferSyntax)
    ) {
        throw new Error(
            `decompressJpegLosslessInDataset called with unsupported transfer syntax '${String(
                transferSyntax
            )}'`
        );
    }

    const bitsAllocated: number = dataset.BitsAllocated;
    if (typeof bitsAllocated !== 'number') {
        throw new Error(
            'BitsAllocated is missing or non-numeric in JPEG Lossless dataset'
        );
    }

    // The decoder derives sample layout from the JPEG stream, not the DICOM
    // header. For colour it emits interleaved RGB (xDim*yDim*numBytes*3),
    // which downstream code would silently consume as monochrome. We only
    // support single-sample (monochrome) CT/MR/PT, so reject anything else.
    const samplesPerPixel: number = dataset.SamplesPerPixel ?? 1;
    if (samplesPerPixel !== 1) {
        throw new Error(
            `JPEG Lossless decoding supports monochrome only; got SamplesPerPixel=${String(
                dataset.SamplesPerPixel
            )}`
        );
    }

    // Rows/Columns are needed to validate the decoded buffer size below.
    const rows: unknown = dataset.Rows;
    const columns: unknown = dataset.Columns;
    if (typeof rows !== 'number' || typeof columns !== 'number') {
        throw new Error(
            'Rows/Columns are missing or non-numeric in JPEG Lossless dataset'
        );
    }

    const frames = readEncapsulatedFrames(dataset.PixelData);
    if (frames.length === 0) {
        throw new Error('Encapsulated PixelData contains no frames');
    }

    const decodedFrames: ArrayBuffer[] = frames.map((f) =>
        decodeOneFrame(f, bitsAllocated)
    );

    // The decoder sizes its output from the JPEG precision, not BitsAllocated,
    // so a header/stream mismatch yields a wrongly-sized buffer that the rest
    // of the pipeline would reinterpret as pixels with no error. Assert the
    // total decoded size matches the DICOM geometry and fail fast if it does
    // not, turning a silent shape mismatch into a safe, actionable skip.
    const expectedBytes =
        rows * columns * samplesPerPixel * (bitsAllocated / 8) * frames.length;
    const actualBytes = decodedFrames.reduce((n, f) => n + f.byteLength, 0);
    if (actualBytes !== expectedBytes) {
        throw new Error(
            `JPEG Lossless decode produced ${actualBytes} bytes, expected ${expectedBytes}`
        );
    }

    if (decodedFrames.length === 1) {
        dataset.PixelData = decodedFrames[0];
        return;
    }

    let totalSize = 0;
    for (const f of decodedFrames) {
        totalSize += f.byteLength;
    }
    const merged = new Uint8Array(totalSize);
    let offset = 0;
    for (const f of decodedFrames) {
        merged.set(new Uint8Array(f), offset);
        offset += f.byteLength;
    }
    dataset.PixelData = merged.buffer;
}
